import { sessionToken } from './auth'
import { storedToken } from './client'
import { apiBase } from './region'

/**
 * Очередь намерений, которые нельзя терять.
 *
 * Окно, заметки и анкета досылаются сами: у обмена есть пометка «не ушло» и повтор
 * при возврате сети. А вот одиночные действия — «выключил уведомления», «тур показан» —
 * отправлялись в один заход: не получилось, и мы об этом больше не вспоминали. Человек
 * выключал уведомления в метро, выходил на воздух, а сервер продолжал их слать.
 *
 * Поэтому такие действия сначала записываются на устройство и только потом уходят.
 * Запись живёт, пока сервер не подтвердит; попытки повторяются при запуске и когда
 * появилась сеть.
 */

/** Ключ устройства, не малыша: намерения общие. */
const KEY = 'moonwake.outbox'

export type Intent = 'push_off' | 'tour_seen'

/** Чем подписан запрос: отписка — ключом устройства, тур — ключом человека. */
const МАРШРУТЫ: Record<Intent, { path: string; method: 'POST' | 'DELETE'; ключ: 'device' | 'session' }> = {
  push_off: { path: '/push', method: 'DELETE', ключ: 'device' },
  tour_seen: { path: '/account/tour-seen', method: 'POST', ключ: 'session' },
}

function прочитать(): Intent[] {
  try {
    const сырое = globalThis.localStorage?.getItem(KEY)
    const список: unknown = сырое === null || сырое === undefined ? [] : JSON.parse(сырое)
    return Array.isArray(список) ? список.filter((item): item is Intent => item === 'push_off' || item === 'tour_seen') : []
  } catch {
    return []
  }
}

function записать(список: Intent[]): void {
  try {
    if (список.length === 0) globalThis.localStorage?.removeItem(KEY)
    else globalThis.localStorage?.setItem(KEY, JSON.stringify([...new Set(список)]))
  } catch {
    // Приватный режим: намерение проживёт только этот сеанс — лучше, чем ничего
  }
}

/** Запомнить намерение и сразу попробовать отправить. */
export async function remember(intent: Intent): Promise<void> {
  записать([...прочитать(), intent])
  await flush()
}

/** Отправить всё, что ждёт. Что не ушло — остаётся ждать следующего раза. */
export async function flush(): Promise<void> {
  const ждут = прочитать()
  if (ждут.length === 0) return
  const остались: Intent[] = []
  for (const intent of ждут) {
    const маршрут = МАРШРУТЫ[intent]
    const token = маршрут.ключ === 'device' ? storedToken() : sessionToken()
    if (token === null) continue // рассказывать некому: ни устройства, ни аккаунта
    try {
      const response = await fetch(`${apiBase()}${маршрут.path}`, {
        method: маршрут.method,
        headers: { authorization: `${маршрут.ключ === 'device' ? 'Device' : 'Session'} ${token}` },
      })
      // 401 и 404 — сервер нас понял по-своему, повторять незачем
      if (!response.ok && response.status >= 500) остались.push(intent)
    } catch {
      остались.push(intent)
    }
  }
  записать(остались)
}

/** Подключить повторы: при возврате сети и при возвращении на вкладку. */
export function watchOutbox(): void {
  globalThis.addEventListener('online', () => void flush())
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void flush()
  })
}
